import { useMemo } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { calculateFeatureWTP } from '../../lib/calculations';
import { formatPrice, pnlColorClass } from '../../lib/formatters';
import { InfoButton } from '../common/Tooltip';
import { Package } from 'lucide-react';

export function PackageWTPSummary() {
  const packages = useSimulatorStore(s => s.packages);
  const assumptions = useSimulatorStore(s => s.assumptions);

  const packageData = useMemo(() => {
    return packages.map(pkg => {
      const totalWTP = pkg.enabledFeatures.reduce((sum, f) => {
        return sum + calculateFeatureWTP(f, pkg.price, assumptions);
      }, 0);
      const totalCOGS = pkg.enabledFeatures.reduce((sum, f) => {
        return sum + (assumptions.featureCosts?.[f] ?? 0);
      }, 0);
      const priceToValue = totalWTP > 0 ? pkg.price / totalWTP : 0;

      return {
        name: pkg.name,
        price: pkg.price,
        featureCount: pkg.enabledFeatures.length,
        totalWTP: Math.round(totalWTP),
        totalCOGS,
        netFeatureValue: Math.round(totalWTP) - totalCOGS,
        priceToValue,
        surplus: Math.round(totalWTP) - pkg.price,
      };
    });
  }, [packages, assumptions]);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <Package size={16} className="text-chart-blue" />
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Package Value Analysis</h3>
        <InfoButton helpKey="priceValueMap" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">
        Total perceived feature value (WTP) vs. package price. A positive surplus means the package offers more value than its price — good for demand. A negative surplus means the price exceeds perceived value.
      </p>

      <div className="space-y-3">
        {packageData.map((pkg, i) => {
          const maxBar = Math.max(pkg.totalWTP, pkg.price, 1);
          const wtpWidth = (pkg.totalWTP / maxBar) * 100;
          const priceWidth = (pkg.price / maxBar) * 100;

          return (
            <div key={i} className="px-4 py-3 rounded-xl" style={{ backgroundColor: 'var(--color-surface)' }}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-sm font-semibold text-primary">{pkg.name}</span>
                  <span className="text-xs text-muted ml-2">{pkg.featureCount} features</span>
                </div>
                <div className={`text-sm font-mono font-bold ${pnlColorClass(pkg.surplus)}`}>
                  {pkg.surplus >= 0 ? '+' : ''}{formatPrice(pkg.surplus)} surplus
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-micro text-muted w-16 shrink-0">WTP</span>
                  <div className="flex-1 h-5 rounded overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
                    <div className="h-full rounded bg-chart-teal/60 flex items-center px-2 transition-all" style={{ width: `${Math.max(wtpWidth, 5)}%` }}>
                      <span className="text-micro font-mono text-primary font-medium">{formatPrice(pkg.totalWTP)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-micro text-muted w-16 shrink-0">S2 Price</span>
                  <div className="flex-1 h-5 rounded overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
                    <div className="h-full rounded bg-chart-blue/60 flex items-center px-2 transition-all" style={{ width: `${Math.max(priceWidth, 5)}%` }}>
                      <span className="text-micro font-mono text-primary font-medium">{formatPrice(pkg.price)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 mt-2 text-micro text-muted">
                <span>Feature COGS: <span className="font-mono text-primary">{formatPrice(pkg.totalCOGS)}</span></span>
                <span>Net Feature Value: <span className={`font-mono font-medium ${pnlColorClass(pkg.netFeatureValue)}`}>{formatPrice(pkg.netFeatureValue)}</span></span>
                <span>Price/Value: <span className="font-mono font-medium text-primary">{pkg.priceToValue.toFixed(1)}x</span></span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
